// The write layer for cycle creation: create_reporting_cycle(_period_label, _period_type,
// _params) — the v1 manual-orchestration RPC (0003c §4a). There is no Phase-4 worker in v1,
// so a human states the period here and the cursor starts at snapshot/draft.
//
// FAIL-CLOSED SERVER-SIDE, and narrower than the read gate: Investment OR Operations ONLY.
// Admins can read the island but CANNOT create a cycle — the RPC raises
// insufficient_privilege. The client disables the button for admins as UX; the RPC is the wall.
//
// IDEMPOTENT on period_label (UNIQUE): re-running for an existing label returns that cycle's
// id rather than erroring — but note the RPC validates _params BEFORE the idempotent return,
// so a replay that omits the compute window still raises. That is deliberate upstream: the
// caller would be passing params that could not have created the cycle.
//
// _params carries FOUR keys and every one of them matters:
//   as_of_date       REQUIRED — reporting_cycles.as_of_date is NOT NULL with no default.
//   window_start     REQUIRED — the compute window is NEVER derived from as_of_date. It
//   window_end       REQUIRED   defines the trade-date window, the temporal quarantine and
//                               the R day-count time-weighting. Before 0003c demanded them,
//                               a cycle created without them died at the snapshot stage.
//   is_webinar_month DERIVED  — see deriveIsWebinarMonth in lib/investment-reporting/period.
//                               Sent explicitly (the RPC defaults it to false, and false is
//                               wrong for every quarter), but never chosen by a human.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ReportingPeriodType } from "@/lib/investment-reporting/period";

export interface CreateReportingCycleInput {
  periodLabel: string;
  periodType: ReportingPeriodType | string;
  asOfDate: string;
  windowStart: string;
  windowEnd: string;
  isWebinarMonth: boolean;
}

export function useCreateReportingCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateReportingCycleInput) => {
      const { data, error } = await (supabase as any).rpc("create_reporting_cycle", {
        _period_label: input.periodLabel,
        _period_type: input.periodType,
        _params: {
          as_of_date: input.asOfDate,
          window_start: input.windowStart,
          window_end: input.windowEnd,
          is_webinar_month: input.isWebinarMonth,
        },
      });
      if (error) throw error;
      // RETURNS uuid — the new (or pre-existing) cycle id.
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["list_reporting_cycles"] });
    },
  });
}
